import store from '@/store';
import { getGridOperators } from '@/services/gridoperators';
import { isValue, isNumeric, isUrl, isEmail,  unicodeString, isFloat, required, isValidPostalcode, isValidLatitude, isValidLongitude, isValidEAN, isValidQRCode, isPositive } from '@/services/validation'

const sort = (array, keyValuePair, sortBy) => {
  const sortField = sortBy || 'label'
  if (!array) return array
  return array.sort((a, b) => keyValuePair
    ? a[sortField].localeCompare(b[sortField])
    : a.localeCompare(b)
  )
}

export function getMunicipalities() {
  if (['vlaanderen', 'amsterdam'].includes(process.env.VUE_APP_TENANT)) {
    return store.getters['tenant/getMunicipalityOptions']
  }
  return store.getters['tenant/getAllMunicipalityOptions'].sort((a, b) => a.text.localeCompare(b.text))
}

export function getProvinces() {
  const tenantsBE = ['engie', 'mechelen', 'vlaanderen']
  const belgian = [
    { value: 'Antwerp', text: 'Antwerp' },
    { value: 'East Flanders', text: 'East Flanders' },
    { value: 'Flemish Brabant', text: 'Flemish Brabant' },
    { value: 'Limburg', text: 'Limburg' },
    { value: 'West Flanders', text: 'West Flanders' },
    { value: 'Hainaut', text: 'Hainaut' },
    { value: 'Liège', text: 'Liège' },
    { value: 'Luxembourg', text: 'Luxembourg' },
    { value: 'Namur', text: 'Namur' },
    { value: 'Walloon Brabant', text: 'Walloon Brabant' }
  ]
  return tenantsBE.includes(process.env.VUE_APP_TENANT)
  ? belgian
  : [
    { value: 'Drenthe', text: 'Drenthe' },
    { value: 'Flevoland', text: 'Flevoland' },
    { value: 'Friesland', text: 'Friesland' },
    { value: 'Gelderland', text: 'Gelderland' },
    { value: 'Groningen', text: 'Groningen' },
    { value: 'Limburg', text: 'Limburg' },
    { value: 'Noord-Brabant', text: 'Noord-Brabant' },
    { value: 'Noord-Holland', text: 'Noord-Holland' },
    { value: 'Overijssel', text: 'Overijssel' },
    { value: 'Utrecht', text: 'Utrecht' },
    { value: 'Zeeland', text: 'Zeeland' },
    { value: 'Zuid-Holland', text: 'Zuid-Holland' }
  ];
}

export function gridOperator () {
  return getGridOperators().sort((a, b) => a.text.localeCompare(b.text))
}

export function getContractors() {
  const contractors = store.getters['tenant/getEnabledContractors'];
  return contractors.map(contractor => ({
    value: contractor.uuid,
    text: contractor.label
  }));
}

export function validationType(type) {
  switch (type) {
    case 'required':
      return (value) => value !== 'Cancelled' && required(value)
    case 'isNumeric':
      return (value) =>  !isValue(value) || isNumeric(value)
    case 'isUrl':
      return (value) =>  !isValue(value) || isUrl(value)
    case 'isEmail':
      return (value) =>  !isValue(value) || isEmail(value)
    case 'isFloat':
      return (value) =>  !isValue(value) || isFloat(value)
    case 'unicodeString':
      return (value) =>  !isValue(value) || unicodeString(value)
    case 'isValidPostalcode':
      return (value) =>  !isValue(value) || isValidPostalcode(value.trim())
    case 'isValidLatitude':
      return (value) =>  !isValue(value) || isValidLatitude(value)
    case 'isValidLongitude':
      return (value) =>  !isValue(value) || isValidLongitude(value)
    case 'isValidEAN':
      return (value) => !isValue(value) || isValidEAN(value)
    case 'isValidQRCode':
      return (value) => !isValue(value) || isValidQRCode(value)
    case 'isPositive':
      return (value) => !isValue(value) || isPositive(value)
    case 'requiredOnComplete':
      return () => true

    default:
      return true
  }
}

export function setValidations(form) {
  for (let part of form) {
    if (part.validation) {
      Object.keys(part.validation).forEach(key => {
        const validationArray = part.validation[key];
        if (Array.isArray(validationArray)) {
          const validationObject = {};
          validationArray.forEach(validation => {
            validationObject[validation] = validationType(validation);
          });
          part.validation[key] = validationObject;
        }
      })
    }
  }
}

export function getPids() {
  const pids = store.getters['tenant/getPIDs'];
  return pids.filter(pid => pid.enabled && !pid.deleted_at).map(pid => ({
    value: pid.uuid,
    text: pid.label
  }));
}

export function setMunicipalitiesDropdown() {
  const municipalities = store.getters['tenant/getMunicipalityOptions']
  const options = municipalities.filter(mun => mun.text).map(mun => ({ value: mun.value, text: mun.text }))

  return sort(options, true, 'text')
}

export function setGridOperatorsFromMunicipalities() {
  const operators = Array.from(new Map([
    ...store.getters['tenant/getMunicipalityOptions'].map(o => Object.values(o.gridOperator))
  ]))
  return sort(operators.map(([value, text]) => ({ value, text })), true, 'text')
}

export function setCpos() {
  return store.getters['tenant/getCpos']
}

export function setContractorsFromStakeholders() {
  const contractors = store.getters['tenant/getContractors'].map(c => ({ value: c.value, text: c.label }))
  return sort(contractors, true, 'text')
}

export function setWorkflowUsersDropdown() {
  const workflowUsers = store.getters['tenant/getSelectableWorkflowUsers']
  return sort(workflowUsers, true, 'text')
}

export function setCharchingStationVersions() {
  return sort(store.getters['tenant/getCharchingStationVersions'])
}

export function setCharchingStationVersionOptions() {
  const versions = store.getters['tenant/getCharchingStationVersions'].map(value => ({ value, text: value }))
  return sort(versions, true, 'text')
}
